import React, { CSSProperties, ReactNode, useMemo } from 'react';
import { createPortal } from 'react-dom';
import cl from 'classnames';
import { useListPosition } from 'src/hooks';
import { uuidV4 } from 'src/helper';
import { FieldValue } from 'src/utils/fieldHandler';
import styles from './selectStyle.module.scss';

export type SelectProps = {
  value: FieldValue;
  items: {
    value: FieldValue;
    label: string;
    inactive?: boolean;
    isCb?: boolean;
    count?: number;
  }[];
  selectItem: (value: FieldValue) => void;
  selectedCbIDs?: Set<string>;
  selectCbItem?: (id: string, value: boolean) => void;
  design?: 'default' | 'border' | 'switcher' | 'switcherBorder';
  disabled?: boolean;
  isLoading?: boolean;
  excludeCurrent?: boolean;
  expandOnTop?: boolean;
  className?: string;
  style?: CSSProperties;
  icon?: ReactNode;
};

type ArrowIconProps = {
  className: string;
  design: SelectProps['design'];
};

const ArrowIcon = ({ design, className }: ArrowIconProps) => {
  return design === 'switcherBorder' ? (
    <svg
      width="16"
      height="9"
      viewBox="0 0 16 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12.0381 2L7.66309 6.27701L3.28809 2"
        stroke="#1F93FF"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg
      width="9"
      height="9"
      viewBox="0 0 9 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M6.82056 2.87012L4.30774 5.62225L1.79492 2.87012"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const Select = ({
  value,
  items,
  selectItem,
  design = 'default',
  disabled = false,
  className,
  style,
  isLoading = false,
  excludeCurrent = false,
  expandOnTop = false,
  icon,
  selectedCbIDs,
  selectCbItem,
}: SelectProps) => {
  const uuid = useMemo(() => uuidV4(), []);
  const { isView, toHide, btnRef, portalRef, onBtnClick, listPosition } =
    useListPosition(expandOnTop);

  const currentItem = useMemo(
    () => items.find(({ value: itemValue }) => itemValue === value),
    [items, value],
  );

  const currentLabel = useMemo(
    () => currentItem?.label ?? (value === null ? '---' : String(value)),
    [currentItem?.label, value],
  );

  const isInactive = useMemo(
    () => (currentItem ? Boolean(currentItem.inactive) : false),
    [currentItem],
  );

  const filteredItems = useMemo(
    () =>
      excludeCurrent
        ? items.filter(({ value: itemValue }) => value !== itemValue)
        : items,
    [excludeCurrent, items, value],
  );

  const isDisabled = useMemo(
    () => disabled || !currentItem,
    [disabled, currentItem],
  );

  return (
    <div
      ref={btnRef}
      onClick={(event) => {
        if (isLoading || isDisabled) {
          return;
        }

        onBtnClick(event);
      }}
      className={cl(
        styles.button,
        styles[design],
        isDisabled && styles.disabled,
        className,
      )}
      style={style}
    >
      {isLoading && (
        <div className={styles.loading}>
          <div className={styles.loader} />
        </div>
      )}
      {icon && <div className={styles.icon}>{icon}</div>}
      <div
        className={cl(
          styles.label,
          isInactive && styles.inactive,
          icon && styles.withIcon,
        )}
      >
        {isLoading ? '' : currentLabel}
      </div>
      <ArrowIcon
        className={cl(
          styles.arrowIcon,
          isView === Boolean(expandOnTop) && styles.expanded,
          !['switcherBorder', 'switcher'].includes(design) &&
            styles.notSwitcher,
        )}
        design={design}
      />
      {listPosition &&
        createPortal(
          <div className={styles.portal} onClick={toHide} ref={portalRef}>
            <div
              className={styles.relativeContainer}
              onClick={toHide}
              style={{ ...listPosition }}
            >
              <div
                className={cl(
                  styles.absoluteContainer,
                  expandOnTop && styles.expandOnTop,
                )}
              >
                <div
                  className={cl(styles.listWrapper, isView && styles.expanded)}
                  style={{ width: listPosition.width }}
                >
                  <div
                    className={styles.list}
                    style={{ width: listPosition.width }}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {filteredItems.map(
                      ({ value: itemValue, label, inactive, count, isCb }) =>
                        isCb ? (
                          <div
                            key={`select-${uuid}-${itemValue}`}
                            className={cl(
                              styles.listItem,
                              Boolean(
                                selectedCbIDs?.has(itemValue as string),
                              ) && styles.current,
                              inactive && styles.inactive,
                            )}
                            onClick={(event) => {
                              if (inactive) {
                                event.stopPropagation();
                                return;
                              }

                              selectCbItem?.(
                                itemValue as string,
                                !selectedCbIDs?.has(itemValue as string),
                              );
                            }}
                          >
                            <div
                              className={cl(
                                styles.checkbox,
                                Boolean(
                                  selectedCbIDs?.has(itemValue as string),
                                ) && styles.checked,
                              )}
                            >
                              <svg
                                width="16"
                                height="17"
                                viewBox="0 0 16 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M6.0001 11.2799L3.2201 8.4999L2.27344 9.43989L6.0001 13.1666L14.0001 5.16656L13.0601 4.22656L6.0001 11.2799Z"
                                  fill="white"
                                />
                              </svg>
                            </div>
                            {label}
                          </div>
                        ) : (
                          <div
                            key={`select-${uuid}-${itemValue}`}
                            className={cl(
                              styles.listItem,
                              value === itemValue && styles.current,
                              inactive && styles.inactive,
                            )}
                            onClick={(event) => {
                              if (inactive) {
                                event.stopPropagation();
                                return;
                              }

                              toHide(event);
                              selectItem(itemValue);
                            }}
                          >
                            {label}
                            {count !== undefined && (
                              <span
                                className={cl(
                                  styles.count,
                                  count === 0 && styles.zero,
                                )}
                              >
                                {count}
                              </span>
                            )}
                          </div>
                        ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Select;
